import React, { useEffect, useMemo } from 'react';
import {
  groupBy, isEmpty, map, sortBy, filter, forEach, max,
} from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import CheckoutComponent from '../components/Checkout';
import useShoppingCart from '../contexts/shoppingCart';
import withTapPay from '../../hocs/withTapPay';
import { useProgressContext } from '../../preorder/contexts/useProgressContext';
import preOrderAPI from '../../preorder/api/preOrder';
import { checkout } from '../api/menu';
import { useAuthState } from '../../preorder/plugins/Auth';
import { PAYMENT_METHOD } from '../../preorder/containers/CheckoutPage/components/Checkout';
import { paymentMethodMap } from './PreOrder';
import { genGroupId } from './ShoppingCart';

export default withTapPay(() => {
  const history = useHistory();
  const { state: { itemIds, shopCampaignDiscounts, items } = {} } = useLocation();
  const { shoppingCart, removeItem } = useShoppingCart();
  const { token } = useAuthState();
  const { start: startProgress, end: endProgress } = useProgressContext();

  useEffect(() => {
    if (isEmpty(itemIds)) history.push('/menu/shopping-cart');
  }, [itemIds, history]);

  const data = useMemo(
    () => sortBy(
      filter(
        map(
          groupBy(shoppingCart, genGroupId),
          (value, id) => {
            const [item] = value || [];

            return {
              id,
              data: item,
              amount: value.length,
              items: value,
            };
          }
        ),
        ({ id } = {}) => (itemIds || []).includes(id)
      ),
      'id'
    ),
    [shoppingCart, itemIds]
  );

  const dataToDisplay = useMemo(() => {
    const result = {};

    forEach(sortBy(items, 'id'), (item) => {
      const id = genGroupId(item);

      const {
        fridgeId,
        fridgeName,
        locationPrompt,
      } = item || {};

      result.id = fridgeId;
      result.name = fridgeName;
      result.description = locationPrompt;

      if (!result.itemDict) {
        result.itemDict = {};
      }

      if (!result.itemDict[id]) {
        result.itemDict[id] = {
          ...item,
          id,
          items: [],
        };
      }

      result.itemDict[id].items.push(item);
    });

    return result;
  }, [items]);

  const onCheckoutButtonClick = async (orderData) => {
    let totalPrice = 0;
    const {
      paymentInfo, paymentMethod, couponVoucherId, discount,
    } = orderData || {};

    startProgress();

    try {
      if (!paymentMethod) throw new Error('請選擇支付方式');

      const itemsToCheckout = data.map((item) => {
        const { data: dataItem, amount: buyCount } = item || {};
        const {
          arrangedTime,
          fridgeId,
          id: productId,
          name: productName,
          onlineShopId,
          options,
          price,
          productNumber,
          provider: providerName,
          providerImage,
          redeemStartTime,
          sellPrice,
        } = dataItem || {};

        totalPrice += (sellPrice || price) * buyCount;

        const redeemStartTimestamp = +moment(redeemStartTime).utcOffset(8).startOf('d');

        return {
          arrangedTime,
          buyCount,
          fridgeId,
          options,
          price: sellPrice || price,
          productId,
          productName,
          productNumber,
          providerImage,
          providerName,
          redeemStartTime: redeemStartTimestamp,
          ...(onlineShopId && { onlineShopId }),
        };
      });

      totalPrice -= (discount || 0);

      totalPrice = max([totalPrice, 0]);

      if (paymentMethod === PAYMENT_METHOD.LINE_PAY) {
        const result = await preOrderAPI.createLinePayRequestInfo({
          body: {
            token,
            linePayRedirectUrl: `${window.location.origin}/pre-orders/checkout/line-pay-payment?submitLinePay=true`,
            preChargeAmount: totalPrice,
          },
        });

        const { status, redirectUrl } = result || {};

        if (status !== 'OK') throw new Error('LINE PAY 交易失敗，請聯繫客人員。');
        if (!redirectUrl) throw new Error('LINE PAY 交易失敗，請聯繫客人員。');

        window.localStorage.setItem('checkoutSummary', JSON.stringify({
          couponVoucherId,
          discount,
          from: 'DF_PO',
          items: itemsToCheckout,
          totalPrice,
        }));

        removeItem(data.map((item) => item.items).flat());

        const { search, pathname, origin: redirectOrigin } = new URL(redirectUrl);

        if (window.location.origin === redirectOrigin) {
          history.replace(`${pathname}${search}`);

          endProgress();

          return false;
        }

        window.location.href = redirectUrl;

        return false;
      }

      const checkoutResult = await checkout({
        couponVoucherId,
        items: itemsToCheckout,
        paymentInfo,
        paymentMethod: paymentMethodMap[paymentMethod],
        token,
      });
      const { status, orderId, message } = checkoutResult || {};

      if (status !== 'success') throw new Error(message || '結帳失敗，請聯繫客人員。');

      removeItem(data.map((item) => item.items).flat());

      history.replace('/menu/checkout/receipt', {
        discount,
        items: itemsToCheckout,
        orderId,
        totalPrice,
        urlToGo: '/menu',
      });

      endProgress();
      return true;
    } catch (e) {
      endProgress();
      throw e;
    }
  };

  return (
    <CheckoutComponent
      data={data}
      dataToDisplay={dataToDisplay}
      items={items}
      onCheckoutButtonClick={onCheckoutButtonClick}
      shopCampaignDiscounts={shopCampaignDiscounts}
    />
  );
});
